import {StipTypes} from '@deliveriesondemand/dod-data';
import {createSelector} from 'reselect';

import {getIsolatedStipType} from '-/util/util';
import {capitalize, sortBy} from '-/util/lodash';

export const applicantStipObjectSelector = createSelector(
    state => state.stips.applicantType,
    state => state.stips.stipsData,
    (applicantType = 'app', stipsData) => stipsData[applicantType]
);

export const oppositeApplicantStipObjectSelector = createSelector(
    state => state.stips.applicantType,
    state => state.stips.stipsData,
    (applicantType, stipsData) => stipsData[applicantType === 'app' ? 'coApp' : 'app']
);

/**
 * This will return a filtered array of the stip types for the applicant type
 * If in "isolation mode", it will only display the individual stip that needs to be collected
 */
export const stipListSelector = createSelector(
    applicantStipObjectSelector,
    getIsolatedStipType,
    (appStip, isolatedStipType) => {
        const {stips = []} = appStip || {};

        return stips.filter(stip => {
            if (isolatedStipType) {
                if (Array.isArray(isolatedStipType)) {
                    return isolatedStipType.includes(stip.stipType);
                } else {
                    return stip.stipType === isolatedStipType;
                }
            } else {
                return true;
            }
        });
    }
);

export const oppositeStipListSelector = createSelector(
    oppositeApplicantStipObjectSelector,
    getIsolatedStipType,
    (appStip, isolatedStipType) => {
        const {stips = []} = appStip || {};

        return stips.filter(stip => {
            if (isolatedStipType) {
                if (Array.isArray(isolatedStipType)) {
                    return isolatedStipType.includes(stip.stipType);
                } else {
                    return stip.stipType === isolatedStipType;
                }
            } else {
                return true;
            }
        });
    }
);

export const getAllStipsForApplicantSelector = createSelector(
    stipListSelector,
    (stips) => {
        const stipTypesKeys = Object.keys(StipTypes);
        const allStips = stips.map(stip => {
            return {
                docType: stip.stipType,
                ...stip,
                idx: stipTypesKeys.indexOf(stip.stipType)
            };
        });

        return sortBy(allStips, 'idx');
    }
);

export const getStipsDocsToResubmitSelector = createSelector(
    stipListSelector,
    (stips) => {
        const stipsDocs = stips.map(stip => {
            const {docs = []} = stip;
            return docs.map((doc, index) => {
                return {
                    docType: stip.stipType,
                    index,
                    ...doc
                };
            }).filter(doc => doc.status === 'REJECTED');
        });
        return [].concat.apply([], stipsDocs);
    }
);

export const getNumberStipsToUploadSelector = createSelector(
    stipListSelector,
    (stips) => {
        let nSubmitted = 0;
        let nActive = 0;
        let stipsUploadedThisSession = 0;
        stips.forEach(stip => {
            stip.docs.forEach(doc => {
                if (doc.submitted) {
                    stipsUploadedThisSession++;
                }
                if (doc.submitted ||
                    doc.status === 'SUBMITTED' ||
                    doc.status === 'VERIFIED' ||
                    doc.status === 'UNDERREVIEW') {
                    nSubmitted++;
                }
                if (doc.status !== 'NOTREQUIRED') {
                    nActive++;
                }
            });
        });
        return {
            stipsUploaded: nSubmitted,
            stipsTotal: nActive,
            stipsUploadedThisSession
        };
    }
);

export const areThereStipsToResubmitSelector = createSelector(
    getStipsDocsToResubmitSelector,
    stipsDocs => stipsDocs.length > 0
);

export const allStipsVerfifiedForApplicantSelector = createSelector(
    stipListSelector,
    (stips) => {
        for (let i = 0; i < stips.length; i++) {
            const stip = stips[i];
            for (let j = 0; j < stip.docs.length; j++) {
                const doc = stip.docs[j];

                if (doc.status === 'NOTREQUIRED') {
                    continue;
                }

                if (doc.status !== 'VERIFIED') {
                    return false;
                }
            }
        }

        return true;
    }
);

export const allStipsSubmittedForApplicantSelector = createSelector(
    stipListSelector,
    (stips) => {
        for (let i = 0; i < stips.length; i++) {
            const stip = stips[i];
            for (let j = 0; j < stip.docs.length; j++) {
                const doc = stip.docs[j];

                if (doc.status === 'NOTREQUIRED') {
                    continue;
                }

                if (['VERIFIED', 'SUBMITTED', 'UNDERREVIEW'].indexOf(doc.status) === -1) {
                    return false;
                }
            }
        }

        return true;
    }
);

export const allStipsSubmittedSelector = createSelector(
    stipListSelector,
    oppositeStipListSelector,
    (stips, oppositeStips) => {
        for (let i = 0; i < stips.length; i++) {
            const stip = stips[i];
            for (let j = 0; j < stip.docs.length; j++) {
                const doc = stip.docs[j];

                if (doc.status === 'NOTREQUIRED') {
                    continue;
                }

                if (['VERIFIED', 'SUBMITTED', 'UNDERREVIEW'].indexOf(doc.status) === -1) {
                    return false;
                }
            }
        }

        for (let i = 0; i < oppositeStips.length; i++) {
            const stip = oppositeStips[i];
            for (let j = 0; j < stip.docs.length; j++) {
                const doc = stip.docs[j];

                if (doc.status === 'NOTREQUIRED') {
                    continue;
                }

                if (['VERIFIED', 'SUBMITTED', 'UNDERREVIEW'].indexOf(doc.status) === -1) {
                    return false;
                }
            }
        }

        return true;
    }
);

const isUpperCase = str => {
    if (!str) {
        return false;
    }
    
    return str === str.toUpperCase();
};

const toProperCase = name => {
    if (isUpperCase(name)) {
        return capitalize(name);
    }

    return name;
};

const shortNameForBuyerInfo = (buyerInfo = {}) => {
    const {shortName, name} = buyerInfo;
    if (shortName) {
        return toProperCase(shortName);
    }

    try {
        const [value] = name.trim().split(' ');

        return toProperCase(value);
    } catch (e) {
        return toProperCase(name);
    }
};

export const applicantNameSelector = createSelector(
    state => state.stips.applicantType,
    state => state.buyerInfo,
    (applicantType = 'app', buyerInfo) => {
        const buyerInfoKey = applicantType === 'app' ? 'buyerInfo' : 'buyerInfo_CoApp';
        return shortNameForBuyerInfo(buyerInfo[buyerInfoKey]);
    }
);

export const oppositeApplicantNameSelector = createSelector(
    state => state.stips.applicantType,
    state => state.buyerInfo,
    (applicantType = 'app', buyerInfo) => {
        const buyerInfoKey = applicantType === 'app' ? 'buyerInfo_CoApp' : 'buyerInfo';
        return shortNameForBuyerInfo(buyerInfo[buyerInfoKey]);
    }
);

export const mainApplicantNameSelector = createSelector(
    state => state.buyerInfo,
    (buyerInfo) => {
        return shortNameForBuyerInfo(buyerInfo['buyerInfo']);
    }
);

export const findImageData = (state, docType, indexOfDoc) => {
    const {applicantType, stipsImages} = state.stips;
    return stipsImages[`${docType}_${applicantType}_${indexOfDoc}`];
};
